<template>
  <div class="step-question-template multiple-choice-step-question" v-if="isUs">
    <div class="question-text" v-html="question.questionText"></div>
    <div class="question-description" v-html="question.questionDescription" v-if="question.questionDescription"></div>
    <YesNo v-if="isYesNo" v-model="answer" yes="Yes" no="No" :id="question.id" />

    <MultipleChoiceOptions v-model="answer" v-if="!isYesNo" :options="question.options" :id="question.id" />

    <div v-if="freeTextRequired">
      <div class="question-text" role="heading" aria-level="4" style="margin-top: 20px">
        {{ question.freeTextQuestion }}
      </div>
      <div class="question-description">
        {{ question.freeTextQuestionDescription }}
      </div>
      <textarea
        class="question-additional-notes rounded-md"
        :class="[!!errorMessage ? 'outline outline-2 outline-alert-banner' : '']"
        v-model="freeTextResponse"
        id="free-text-required"
      />
    </div>

    <div class="question-error" v-if="errorMessage">{{ errorMessage }}</div>
    <div class="question-warning" v-if="warningMessage">
      {{ warningMessage }}
    </div>
  </div>
  <div v-else>
    <div class="row question-row question-row-wrapped" :style="first ? { borderTop: 'none' } : {}">
      <div style="word-wrap: break-all">
        <span class="question-text" v-html="question.mainQuestionText"></span>
        <div class="question-description" v-html="question.questionDescription" v-if="question.questionDescription" />
        <template v-if="hasInfoPoints">
          <ul class="question-points points-wrapped">
            <li v-for="(point, idx) in question.infoPoints" :key="idx">
              <span v-html="point" />
            </li>
          </ul>
        </template>
        <MultipleChoiceOptions
          class="mt-2"
          v-model="answer"
          v-if="!isYesNo"
          :options="question.options"
          :id="question.id"
        />
      </div>

      <div>
        <YesNo
          v-if="isYesNo"
          v-model="answer"
          yes="Yes"
          no="No"
          :id="question.id"
          :preventAutocomplete="question.allowPrePopulatedAnswer === false"
        />
      </div>
    </div>

    <div class="row error-row">
      <div
        v-if="errorMessage"
        style="margin-left: 15px; max-width: 80%"
        class="ui-message ui-message-error ui-widget ui-corner-all"
      >
        <span class="ui-message-error-icon"></span>
        <span class="ui-message-error-detail" v-html="errorMessage" />
      </div>

      <div
        v-if="warningMessage"
        style="margin-left: 15px; max-width: 80%"
        class="ui-message ui-message-warn ui-widget ui-corner-all"
      >
        <span class="ui-message-warn-icon" />
        <span class="ui-message-warn-detail" v-html="warningMessage" />
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import Vue from "vue";
import poster from "../../mixins/poster";
import responseAdapters from "../../mixins/responseAdapters";
import validator from "../../mixins/validator";
import YesNo from "../YesNo.vue";
import MultipleChoiceOptions from "./MultipleChoiceOptions.vue";

export default Vue.extend({
  mixins: [poster, validator],
  inject: ["isUs", "submitUrl", "csrfHeader", "csrf"],
  components: {
    YesNo,
    MultipleChoiceOptions,
  },
  props: {
    question: { required: true },
    showRequired: { required: true },
    first: { required: true },
    validate: { required: false, default: true },
  },
  data() {
    return {
      answer: this.question.response,
      initialAnswer: null,
      responseAdapter: responseAdapters.multipleChoice,
      freeTextResponse: this.question.freeTextResponse,
    };
  },
  created() {
    this.$emit("answer", this.question, this.responseAdapter(this.question));
    if (this.question.response != null) {
      this.$emit("update-next-button-status", this.isComplete, !this.isAcceptableAnswer);
      this.hasDefaultAnswer = true;

      this.initialAnswer = this.question.response;
    }
  },
  computed: {
    hasInfoPoints() {
      return this.question.infoPoints && this.question.infoPoints.length;
    },
    isAcceptableAnswer() {
      if (this.answer === null) {
        return null;
      }
      if (this.isYesNo) {
        return this.question.expectYes === null
          ? this.answer === "Yes" || this.answer === "No"
          : this.answer === (this.question.expectYes ? "Yes" : "No");
      }
      return this.question.acceptableValues && this.question.acceptableValues.includes(this.answer);
    },
    valid() {
      return typeof this.errorMessage !== "string";
    },
    optionSpecificHasWarning() {
      if (this.answer) {
        const selectedOption = this.question.options.find((o) => o.value === this.answer);
        if (selectedOption && selectedOption.warningText) return true;
      }

      return false;
    },
    warningMessage() {
      if (this.isAcceptableAnswer === false) {
        return this.question.warning;
      }

      return null;
    },
    errorMessage() {
      if (this.validate && this.isAcceptableAnswer === false) {
        return this.question.rejectReason;
      }

      if (this.showRequired && this.answer === null) {
        return "You must answer the question to continue";
      }

      return null;
    },
    isYesNo() {
      return (
        this.question.questionType !== "SingleSelectQuestion" &&
        this.question.questionType !== "SingleSelectWithFreeTextQuestion" &&
        this.question.questionType !== "EthnicityQuestion"
      );
    },
    moreInfoOptions() {
      if (!this.question.options) {
        return [];
      }
      return this.question.options.filter((option) => option.moreInfo).map((option) => option.value);
    },
    freeTextRequired() {
      return (
        this.answer &&
        this.moreInfoOptions.find((option) => option === this.answer) !== undefined &&
        this.supportsFreeTextInput
      );
    },
    hasFilledFreeText() {
      return this.freeTextResponse != null && this.freeTextResponse.length > 0;
    },
    requiredFreeTextCompleted() {
      return !this.freeTextRequired || this.hasFilledFreeText;
    },
    isComplete() {
      return !!this.question.response && this.requiredFreeTextCompleted;
    },
    supportsFreeTextInput() {
      // only these 2 question types support free text input.
      // Showing it for other question types is misleading as customers (and developer while testing UI) might think that the data will be persisted, while it will not
      const QUESTION_TYPES_SUPPORTING_FTI = ["SingleSelectWithFreeTextQuestion", "MultiSelectQuestion"];
      return QUESTION_TYPES_SUPPORTING_FTI.includes(this.question.questionType);
    },
  },
  methods: {
    onUSAnswer() {
      const allowAutoNext = !this.hasDefaultAnswer && !this.optionSpecificHasWarning;
      const isValid = this.isAcceptableAnswer;

      if (allowAutoNext && isValid && this.isComplete) {
        this.$emit("next");
      } else if (!this.validate) {
        this.$emit("update-next-button-status", this.isComplete, !isValid);
      }
    },
  },
  watch: {
    freeTextResponse(newValue) {
      this.question.freeTextResponse = newValue;

      this.$emit("update-next-button-status", this.isComplete, !this.isAcceptableAnswer);
    },
    answer(newValue) {
      if (_.isNull(this.initialAnswer)) {
        this.initialAnswer = newValue;
      }

      this.question.response = newValue;
      this.question.isChangedAnswer = this.question.isChangedAnswer || newValue !== this.initialAnswer;

      if (!this.freeTextRequired) {
        this.question.freeTextResponse = null;
      }

      this.post(this.responseAdapter(this.question));

      if (this.isAcceptableAnswer === false && this.question.redirectFailure != null) {
        this.question.redirectOnSubmit = this.question.redirectFailure;
      } else {
        this.question.redirectOnSubmit = null;
      }

      if (this.isUs) {
        this.onUSAnswer();
      }
    },
  },
});
</script>
